import { useSocket } from '@folklore/socket';
import React, { useCallback, useContext, useMemo, useState, ReactNode, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { mergeGame } from '../lib/utils';

import { useContest } from './ContestContext';
import { GameProvider } from './GameContext';

interface LiveDisplayContextProps {
    display: Display | null;
    setDisplay: React.Dispatch<React.SetStateAction<Display>>;
    game: Game | null;
    games: Game[] | null;
    setGames: React.Dispatch<React.SetStateAction<Game[]>>;
    leaderboard: Leaderboard | null;
}

const LiveDisplayContext = React.createContext<LiveDisplayContextProps>({
    display: null,
    setDisplay: () => {},
    games: null,
    setGames: () => {},
    leaderboard: null,
    game: null,
});

export function useLiveDisplayContext() {
    return useContext(LiveDisplayContext);
}

// export function useDisplayLeaderboard() {
//     const { display = null } = useLiveDisplayContext() || {};
//     const { leaderboard = null } = display || {};
//     return leaderboard || null;
// }

export function useDisplay() {
    const { display = null } = useLiveDisplayContext() || {};
    return display || null;
}

interface LiveDisplayProviderProps {
    children: ReactNode;
    display: Display;
    followCurrentGame?: boolean;
    refetch?: () => void;
}

export const LiveDisplayProvider: React.FC<LiveDisplayProviderProps> = ({
    children,
    display: initialDisplay,
    followCurrentGame = false,
    refetch = null,
}) => {
    const { locale } = useIntl();
    const { id: contestId = null } = useContest();
    // const url = useUrlGenerator();
    // const queryClient = useQueryClient();

    const [display, setDisplay] = useState<Display>(initialDisplay);
    const { id: displayId = null, game = null } = display || {};
    const { id: gameId = null } = game || {};

    useEffect(() => {
        setDisplay(initialDisplay);
    }, [setDisplay, initialDisplay]);

    const onSocketMessage = useCallback(
        ({ event, data: newData }) => {
            if (event === 'display.updated') {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { socket = null, ...data } = newData || {};

                if (Object.keys(data).length > 0) {
                    const { game: newGame = null } = newData || {};
                    const { game = null } = display || {};
                    setDisplay(
                        game !== null
                            ? { ...display, ...newData, game: mergeGame(game, newGame) }
                            : { ...display, ...newData },
                    );
                } else {
                    refetch();
                }
            }
            // if (event === 'leaderboard.updated') {
            //     setDisplay({ ...display, leaderboard: newData });
            // }
        },
        [display, setDisplay, refetch],
    );

    // console.log('display', display);

    const channels = useMemo(
        () => [`display.${displayId || '_'}.${locale}`].filter((it) => it !== null),
        [locale, gameId, contestId, followCurrentGame],
    );

    useSocket(channels, {
        onMessage: onSocketMessage,
    });

    const value = useMemo(() => ({ display }), [display]);

    if (game !== null) {
        return (
            <LiveDisplayContext.Provider value={value}>
                <GameProvider game={game}>{children}</GameProvider>
            </LiveDisplayContext.Provider>
        );
    }

    return <LiveDisplayContext.Provider value={value}>{children}</LiveDisplayContext.Provider>;
};

export default LiveDisplayContext;
